import { useState, useEffect, useRef } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../utils/stripeConfig";
import { CustomCheckoutForm } from "../components/CustomCheckoutForm";
import { Bluelagoon } from "@bluelagoon";
import { getProductId } from "../utils/billingHelpers";

export function CustomCheckoutModal({
    isOpen,
    onClose,
    selectedPlan,
    isYearly,
    userId,
    currentUser,
    addPaymentMethod,
    updateSubscription,
    paymentMethods = [],
    checkoutType = "subscription", // "subscription" or "credit"
    creditAmount,
    onPurchaseCredit,
    t
}) {
    const [clientSecret, setClientSecret] = useState(null);
    const [setupIntentLoading, setSetupIntentLoading] = useState(false);
    const [setupIntentError, setSetupIntentError] = useState(null);
    const hasTrackedRef = useRef(false);

    // Track checkout opened for abandoned checkout emails
    useEffect(() => {
        if (isOpen && userId && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
            Bluelagoon.trackCheckoutOpened({
                userId,
                planName: selectedPlan?.name,
                productId: selectedPlan ? getProductId(selectedPlan) : undefined,
                isYearly,
                checkoutType,
                creditAmount,
            }).catch((err) => {
                console.error("Error tracking checkout opened:", err);
            });
        }
        if (!isOpen) {
            hasTrackedRef.current = false;
        }
    }, [isOpen, userId]);

    // Fetch SetupIntent clientSecret when modal opens
    useEffect(() => {
        let isCancelled = false;

        if (isOpen && userId && !clientSecret) {
            setSetupIntentLoading(true);
            setSetupIntentError(null);

            Bluelagoon.createSetupIntent({ userId })
                .then((response) => {
                    if (isCancelled) return;
                    // Handle both response structures: response.data.clientSecret or response.data.data.clientSecret
                    const clientSecretValue =
                        response?.data?.clientSecret ||
                        response?.data?.data?.clientSecret ||
                        response?.clientSecret;

                    if (clientSecretValue) {
                        setClientSecret(clientSecretValue);
                    } else {
                        console.error("Setup intent response:", response);
                        setSetupIntentError(response?.error || response?.data?.error || "Failed to initialize payment");
                    }
                })
                .catch((error) => {
                    if (isCancelled) return;
                    console.error("Error creating setup intent:", error);
                    setSetupIntentError(error.message || "Failed to initialize payment");
                })
                .finally(() => {
                    if (!isCancelled) {
                        setSetupIntentLoading(false);
                    }
                });
        }

        // Reset when modal closes
        if (!isOpen) {
            setClientSecret(null);
            setSetupIntentError(null);
        }

        return () => {
            isCancelled = true;
        };
    }, [isOpen, userId, clientSecret]);

    if (!isOpen || (!selectedPlan && !creditAmount)) return null;

    // Elements options for PaymentElement
    const elementsOptions = clientSecret ? {
        clientSecret,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#4F46E5',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '8px',
            },
        },
    } : undefined;

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4 billing-modal-overlay"
            style={{
                position: "fixed",
                zIndex: 9999,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden billing-modal-content flex flex-col"
                style={{
                    maxHeight: "min(90vh, 90dvh, calc(100vh - 16px))",
                    position: "relative",
                    zIndex: 10000,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header - Compact */}
                <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-4 sm:px-8 py-3 sm:py-4 flex-shrink-0">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg sm:text-xl font-bold text-white">
                                {t("billing.secureCheckout") || "Secure Checkout"}
                            </h2>
                            <div className="hidden sm:flex items-center gap-1 text-white/90 text-sm">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span className="text-xs font-medium">SSL encrypted</span>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white text-2xl leading-none w-10 h-10 flex items-center justify-center touch-manipulation transition-colors rounded-full hover:bg-white/10"
                            aria-label={t("common.close") || "Close"}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Checkout Form Content */}
                <div
                    className="p-4 sm:p-6 md:p-8 overflow-y-auto custom-scrollbar flex-1 min-h-0"
                >
                    {setupIntentLoading && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <svg
                                className="animate-spin h-8 w-8 text-indigo-600 mb-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <p className="text-gray-600">{t("billing.initializingPayment") || "Initializing payment..."}</p>
                        </div>
                    )}

                    {setupIntentError && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="ml-3">
                                    <h4 className="text-sm font-bold text-red-800">{t("billing.errors.generic.title") || "Payment Error"}</h4>
                                    <p className="text-sm text-red-700 mt-1">{setupIntentError}</p>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                                    >
                                        {t("common.tryAgain") || "Try again"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!setupIntentLoading && !setupIntentError && clientSecret && (
                        <Elements stripe={stripePromise} options={elementsOptions}>
                            <CustomCheckoutForm
                                selectedPlan={selectedPlan}
                                isYearly={isYearly}
                                onSuccess={() => {
                                    onClose();
                                    window.location.reload();
                                }}
                                onCancel={onClose}
                                userId={userId}
                                addPaymentMethod={addPaymentMethod}
                                updateSubscription={updateSubscription}
                                currentUser={currentUser}
                                paymentMethods={paymentMethods}
                                checkoutType={checkoutType}
                                creditAmount={creditAmount}
                                onPurchaseCredit={onPurchaseCredit}
                                t={t}
                                clientSecret={clientSecret}
                            />
                        </Elements>
                    )}
                </div>
            </div>
        </div>
    );
}
